import ImageQualityStatus from '../../../enums/ImageQualityStatus.js';
import { isAliasingReduction } from '../../../utilities/voxelGrid/index.js';
export function lineHasData(line) {
    if (line.record) {
        return line.record.lowest !== undefined;
    }
    return line.imageQualityStatus !== undefined || !line.fullResolution;
}
export const FIDELITY_STATE_ORDER = [
    'lossy',
    'loading',
    'lod',
    'done',
];
export function fidelityStateRank(state) {
    return FIDELITY_STATE_ORDER.length - FIDELITY_STATE_ORDER.indexOf(state);
}
export function svgStateOf(lines) {
    let worst = 'hidden';
    let worstRank = 0;
    for (const line of lines) {
        if (!lineHasData(line)) {
            continue;
        }
        const kind = lineKind(line);
        const rank = fidelityStateRank(kind);
        if (rank > worstRank) {
            worst = kind;
            worstRank = rank;
        }
    }
    return worst;
}
export function dataKindOf(line) {
    const { record, imageQualityStatus, qualityPending } = line;
    if (!record) {
        return imageQualityStatus !== undefined
            ? dataKindOfImageQuality(imageQualityStatus, qualityPending)
            : 'reduced';
    }
    const spatialKind = spatialKindOf(line);
    if (spatialKind !== 'full') {
        return spatialKind;
    }
    const lowest = record.lowest ?? ImageQualityStatus.FULL_RESOLUTION;
    const lossyDelivery = lowest < ImageQualityStatus.FULL_RESOLUTION;
    if (lossyDelivery && (record.missing > 0 || qualityPending)) {
        return 'loading';
    }
    if (lossyDelivery || isInteractiveLod(standingSampleRatioOf(line))) {
        return 'reduced';
    }
    return 'full';
}
function spatialKindOf(line) {
    const { record, reductionFactors, fullResolution, verdict } = line;
    if (isAliasingReduction(record.reduction)) {
        return 'decimated';
    }
    if (fullResolution && !isReduced(reductionFactors)) {
        return 'full';
    }
    if (!verdict) {
        return 'reduced';
    }
    if (verdict.causes.includes('aliasing')) {
        return 'decimated';
    }
    return verdict.causes.includes('resolution') ? 'reduced' : 'full';
}
export function standingSampleRatioOf(line) {
    return line.sampleDistanceIdle ?? line.sampleDistanceLod;
}
export function interactiveSampleRatioOf(line) {
    return line.sampleDistanceLod / (standingSampleRatioOf(line) || 1);
}
export function dataKindOfImageQuality(status, pending = false) {
    if (status >= ImageQualityStatus.FULL_RESOLUTION) {
        return 'full';
    }
    if (status >= ImageQualityStatus.SUBRESOLUTION) {
        return pending ? 'loading' : 'reduced';
    }
    return 'loading';
}
export function lineKind(line) {
    const dataKind = dataKindOf(line);
    if (dataKind === 'loading') {
        return 'loading';
    }
    if (dataKind !== 'full') {
        return 'lossy';
    }
    return isInteractiveLine(line) ? 'lod' : 'done';
}
export function isInteractiveLine(line) {
    return (isInteractiveLod(interactiveSampleRatioOf(line)) ||
        !!line.interactiveReduction);
}
export function reductionFactorsOf(drawn, full) {
    const factors = [1, 1, 1];
    for (let axis = 0; axis < 3; axis++) {
        const fullSpacing = full.spacing[axis];
        if (!(fullSpacing > 0)) {
            continue;
        }
        factors[axis] = drawn.spacing[axis] / fullSpacing;
    }
    return factors;
}
export function isReduced(factors) {
    return factors.some((factor) => factor > 1 + 1e-6);
}
export function formatFactors(factors) {
    return factors.map(formatReductionFactor).join('×');
}
export function formatReductionFactor(value) {
    const text = formatFactor(value, 2);
    return text === '1.00' && value > 1 ? '<1.01' : text;
}
export function isInteractiveLod(sampleDistanceLod) {
    return sampleDistanceLod > 1 + 1e-2;
}
export function formatFactor(value, digits = 1) {
    const rounded = Math.round(value);
    if (Math.abs(value - rounded) < 1e-6) {
        return String(rounded);
    }
    return value.toFixed(digits);
}
